import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { SendIcon, SparklesIcon, Loader2Icon } from 'lucide-react';
interface Message {
  role: 'user' | 'assistant';
  content: string;
}
export function AIAssistant() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage: Message = {
      role: 'user',
      content: input
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    try {
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: 'You are a luxury home renovation and design expert for Renova8 & Build, a premium renovation company in Brisbane. Provide helpful, professional advice about renovations, interior design, smart home technology, and construction. Keep responses concise and actionable.'
          }, ...messages, userMessage],
          temperature: 0.7,
          max_tokens: 500
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get response');
      }
      const data = await response.json();
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.choices[0].message.content
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: "I apologize, but I'm having trouble responding right now. Please try again later or contact us directly."
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  const suggestedQuestions = ['What are the latest kitchen design trends?', 'How much does a bathroom renovation cost?', 'What smart home features should I consider?', 'How long does a full house renovation take?'];
  return <section id="ai-assistant" className="py-12 sm:py-16 md:py-20 bg-[#0a1628]" ref={ref} data-id="element-43">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-44">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-10 sm:mb-12" data-id="element-45">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-46">
            <SparklesIcon className="w-8 h-8 text-[#d4af37]" data-id="element-47" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white" data-id="element-48">
              AI Design <span className="text-[#d4af37]" data-id="element-49">Assistant</span>
            </h2>
          </div>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto px-4" data-id="element-50">
            Get instant answers to your renovation questions powered by AI
          </p>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="bg-[#0f0f0f] rounded-lg overflow-hidden" data-id="element-51">
          {/* Chat Messages */}
          <div className="h-[400px] overflow-y-auto p-4 sm:p-6 space-y-4" data-id="element-52">
            {messages.length === 0 ? <div className="text-center py-12" data-id="element-53">
                <SparklesIcon className="w-12 h-12 text-[#d4af37] mx-auto mb-4" data-id="element-54" />
                <p className="text-gray-400 mb-6" data-id="element-55">
                  Ask me anything about renovations, design, or smart home
                  technology
                </p>
                <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto" data-id="element-56">
                  {suggestedQuestions.map(question => <button key={question} onClick={() => setInput(question)} className="text-left p-3 bg-[#1c1c1c] rounded-lg text-sm text-gray-300 hover:bg-[#d4af37] hover:text-[#1c1c1c] transition-all duration-300" data-id="element-57">
                      {question}
                    </button>)}
                </div>
              </div> : messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-58">
                  <div className={`max-w-[80%] p-4 rounded-lg ${message.role === 'user' ? 'bg-[#d4af37] text-[#1c1c1c]' : 'bg-[#1c1c1c] text-gray-300'}`} data-id="element-59">
                    <p className="text-sm sm:text-base whitespace-pre-wrap" data-id="element-60">
                      {message.content}
                    </p>
                  </div>
                </div>)}
            {isLoading && <div className="flex justify-start" data-id="element-61">
                <div className="bg-[#1c1c1c] p-4 rounded-lg" data-id="element-62">
                  <Loader2Icon className="w-5 h-5 text-[#d4af37] animate-spin" data-id="element-63" />
                </div>
              </div>}
          </div>
          {/* Input Form */}
          <form onSubmit={handleSubmit} className="border-t border-gray-800 p-4 sm:p-6" data-id="element-64">
            <div className="flex gap-2 sm:gap-4" data-id="element-65">
              <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Ask about renovations, design ideas, costs..." disabled={isLoading} className="flex-1 px-4 py-3 bg-[#1c1c1c] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] transition-all text-sm sm:text-base disabled:opacity-50" data-id="element-66" />
              <button type="submit" disabled={isLoading || !input.trim()} className="px-4 sm:px-6 py-3 bg-[#d4af37] text-[#1c1c1c] rounded-lg font-semibold hover:bg-[#4b4bff] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-67">
                <SendIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-68" />
                <span className="hidden sm:inline" data-id="element-69">Send</span>
              </button>
            </div>
          </form>
        </motion.div>
        <p className="text-center text-gray-500 text-xs sm:text-sm mt-4" data-id="element-70">
          AI-powered responses are for guidance only. Contact us for
          personalized consultations.
        </p>
      </div>
    </section>;
}